import React, { useState, useEffect } from "react";
import "../Zone/Zone.css";
import "../Overview/Overview.css";
import ImageViewer from "../../Components/ImageViewer/ImageViewer";
import { getFileUrl } from "../../Services/storage";

function StorageImage({ path, style, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    if (path.startsWith("http")) { setUrl(path); return; }
    getFileUrl(path).then((r) => setUrl(r.url)).catch(() => {});
  }, [path]);
  if (!url) return <div style={{ ...style, background: "var(--b1)", borderRadius: "4px" }} />;
  return <img src={url} style={style} onClick={onClick} alt="" />;
}

function Thumbnails({ images, onOpen }) {
  if (!Array.isArray(images) || !images.length) return null;
  return (
    <div style={{ display: "flex", gap: "6px", overflowX: "auto", padding: "0 2px 2px" }}>
      {images.map((src, i) => (
        <StorageImage
          key={i}
          path={src}
          onClick={() => onOpen(i)}
          style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px", flexShrink: 0, cursor: "pointer", border: "1px solid var(--b1)" }}
        />
      ))}
    </div>
  );
}

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return (
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) +
    ", " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

const TYPE_VARIANT = {
  EMPLOYEE: "ok",
  LOGISTICS: "info",
  SENSOR: "warn",
  independentEngineer: "neutral",
};

function ProcedureDetail({ className, procedure, onBack, imageOverlayOpen, onOpenImageOverlay }) {
  const [expandedQA, setExpandedQA] = useState(null);
  const [viewer, setViewer] = useState(null);

  const openViewer = (images, startIndex) => {
    setViewer({ images, startIndex });
    onOpenImageOverlay();
  };

  if (!procedure) return null;

  const steps = Array.isArray(procedure.latestUpdate) ? procedure.latestUpdate : [];
  const totalKpi = steps.reduce((s, u) => s + (u.kpi?.final ?? 0), 0);
  const doneSteps = steps.filter((u) => u.success === true).length;
  const type = procedure.procedure?.type;
  const typeVariant = TYPE_VARIANT[type] || "neutral";
  const completedBy = [procedure.employee?.name, procedure.employee?.phoneNumber]
    .filter(Boolean)
    .join(" · ") || "—";
  const pctLabel = procedure.location?.PCTNo
    ? `PCT-${procedure.location.PCTNo} · ${procedure.location.address || ""}`
    : procedure.location?.address || "—";

  return (
    <div className={className} data-cy="procedure-detail-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">Procedure</div>
          <div className="subhdr-title">{procedure.procedure?.title || "—"}</div>
        </div>
        {type && (
          <span className={`snap-pill ${typeVariant}`} style={{ flexShrink: 0 }}>{type}</span>
        )}
      </div>
      <div className="divider" />
      <div className="scroll">

        {/* Location */}
        <div style={{ padding: "8px 20px 0", fontSize: "var(--text-sm)", color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".03em" }}>
          {pctLabel}
        </div>

        {/* Info grid */}
        <div className="stats" style={{ gridTemplateColumns: "1fr 1fr", marginTop: "12px", marginBottom: "0" }}>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Created At</div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--t2)", marginTop: "6px", lineHeight: 1.3 }}>
              {fmtDate(procedure.createdAt)}
            </div>
          </div>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Completed By</div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--t2)", marginTop: "6px", lineHeight: 1.3, wordBreak: "break-word" }}>
              {completedBy}
            </div>
          </div>
          {totalKpi > 0 && (
            <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
              <div className="stat-l">KPI Score</div>
              <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--t1)", marginTop: "6px" }}>
                {totalKpi}
              </div>
            </div>
          )}
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Steps</div>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--t1)", marginTop: "6px" }}>
              {doneSteps} <span style={{ fontSize: "var(--text-sm)", color: "var(--t3)", fontWeight: 400 }}>/ {steps.length}</span>
            </div>
          </div>
        </div>

        {/* Steps */}
        {steps.length > 0 && (
          <>
            <div className="sec-hdr" style={{ marginTop: "16px" }}>
              <div className="t-section">Steps</div>
              <div className="t-label">{steps.length}</div>
            </div>
            <div style={{ padding: "0 20px" }}>
              {steps.map((step, i) => {
                const isLast = i === steps.length - 1;
                const successColor = step.success === true ? "var(--ok)" : step.success === false ? "var(--bad)" : "var(--t3)";
                const hasQA = Array.isArray(step.requiredRemarks) && step.requiredRemarks.length > 0;
                const qaOpen = expandedQA === i;
                const imgPaths = Array.isArray(step.proof) ? step.proof : [];

                return (
                  <div key={i}>
                    {/* Step header */}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", paddingTop: "12px" }}>
                      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: successColor, flexShrink: 0, marginTop: "4px" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                          <div style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--t1)", textTransform: "uppercase", letterSpacing: ".04em" }}>
                            {step.title}
                          </div>
                          {step.success !== undefined && (
                            <span className={`snap-pill ${step.success ? "ok" : "bad"}`} style={{ flexShrink: 0 }}>
                              {step.success ? "Done" : "Failed"}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {step.description && (
                          <div style={{ fontSize: "var(--text-sm)", color: "var(--t3)", marginTop: "4px", lineHeight: 1.4 }}>
                            {step.description}
                          </div>
                        )}

                        {/* Remarks */}
                        {step.remarks && (
                          <div style={{ marginTop: "6px", fontSize: "var(--text-sm)", color: "var(--t2)" }}>
                            <span style={{ color: "var(--t3)" }}>Remarks: </span>{step.remarks}
                          </div>
                        )}

                        {/* KPI (only if no Q&A) */}
                        {!hasQA && step.kpi?.final != null && (
                          <div style={{ marginTop: "6px", fontSize: "var(--text-sm)", color: "var(--t2)" }}>
                            <span style={{ color: "var(--t3)" }}>KPI: </span>{step.kpi.final}
                          </div>
                        )}

                        {/* Q&A collapsible */}
                        {hasQA && (
                          <div style={{ marginTop: "8px" }}>
                            <div
                              onClick={() => setExpandedQA(qaOpen ? null : i)}
                              style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "var(--text-sm)", color: "var(--t2)", userSelect: "none" }}
                            >
                              <span>Q&A · {step.requiredRemarks.length}</span>
                              <svg viewBox="0 0 24 24" style={{ width: "12px", height: "12px", stroke: "currentColor", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", transform: qaOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
                                <polyline points="6,9 12,15 18,9" />
                              </svg>
                              {step.kpi?.final != null && (
                                <span style={{ marginLeft: "auto", color: "var(--t3)" }}>KPI {step.kpi.final}</span>
                              )}
                            </div>
                            {qaOpen && (
                              <div style={{ marginTop: "6px", paddingLeft: "4px", borderLeft: "2px solid var(--b1)" }}>
                                {step.requiredRemarks.map((r, ri) => (
                                  <div key={ri} style={{ padding: "6px 0 6px 8px" }}>
                                    <div style={{ fontSize: "var(--text-xs)", color: "var(--t3)", marginBottom: "2px" }}>{r.title}</div>
                                    <div style={{ fontSize: "var(--text-base)", color: "var(--t1)" }}>{r.value || "—"}</div>
                                    {r.kpi?.final != null && (
                                      <div style={{ fontSize: "var(--text-xs)", color: "var(--t3)", marginTop: "2px" }}>KPI {r.kpi.final}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Images */}
                        {imgPaths.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <Thumbnails
                              images={imgPaths}
                              onOpen={(idx) => openViewer(imgPaths, idx)}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {!isLast && (
                      <div style={{ height: "1px", background: "var(--b1)", margin: "12px 0 0 18px" }} />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ height: "20px" }} />
      </div>

      {imageOverlayOpen && viewer && (
        <ImageViewer
          imagePaths={viewer.images}
          startIndex={viewer.startIndex}
          onClose={onBack}
        />
      )}
    </div>
  );
}

export default ProcedureDetail;
